import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import { TawkChat } from "@/components/chat/TawkChat";

import { lazy, Suspense } from "react";
import * as Sentry from "@sentry/react";

const Index = lazy(() => import("./pages/Index"));
const Products = lazy(() => import("./pages/Products"));
const IdeaGallery = lazy(() => import("./pages/IdeaGallery"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const StandoffKits = lazy(() => import("./pages/StandoffKits"));
const StandoffCaps = lazy(() => import("./pages/StandoffCaps"));
const StandoffBarrels = lazy(() => import("./pages/StandoffBarrels"));
const WireSystems = lazy(() => import("./pages/WireSystems"));
const RodSystems = lazy(() => import("./pages/RodSystems"));
const EdgeGrips = lazy(() => import("./pages/EdgeGrips"));
const Grippers = lazy(() => import("./pages/Grippers"));
const StructureLite = lazy(() => import("./pages/StructureLite"));
const HardwareTools = lazy(() => import("./pages/HardwareTools"));
const Videos = lazy(() => import("./pages/Videos"));
const ExplodedViews = lazy(() => import("./pages/ExplodedViews"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const FAQ = lazy(() => import("./pages/FAQ"));
const MountingGuide = lazy(() => import("./pages/MountingGuide"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Cart = lazy(() => import("./pages/Cart"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const CheckoutCanceled = lazy(() => import("./pages/CheckoutCanceled"));
const MuseumDisplayHardware = lazy(async () => {
  const module = await import("./pages/industries");
  return { default: module.MuseumDisplayHardware };
});
const SignShopHardware = lazy(async () => {
  const module = await import("./pages/industries");
  return { default: module.SignShopHardware };
});

const queryClient = new QueryClient();

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const RouteLoadingFallback = () => (
  <div className="flex min-h-screen items-center justify-center bg-background text-muted-foreground">
    Loading page...
  </div>
);

const ErrorFallback = () => (
  <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6 text-center">
    <h1 className="text-2xl font-semibold text-foreground">Something went wrong</h1>
    <p className="max-w-md text-muted-foreground">
      An unexpected error occurred. Our team has been notified. Please try reloading the page.
    </p>
    <button
      onClick={() => window.location.reload()}
      className="rounded-md bg-primary px-4 py-2 text-primary-foreground hover:opacity-90"
    >
      Reload page
    </button>
  </div>
);

const App = () => (
  <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <TawkChat />

          <Suspense fallback={<RouteLoadingFallback />}>
            <SentryRoutes>
              <Route path="/" element={<Index />} />
              <Route path="/products" element={<Products />} />
              <Route path="/products/standoff-kits" element={<StandoffKits />} />
              <Route path="/products/standoff-caps" element={<StandoffCaps />} />
              <Route path="/products/standoff-barrels" element={<StandoffBarrels />} />
              <Route path="/products/wire-systems" element={<WireSystems />} />
              <Route path="/products/rod-systems" element={<RodSystems />} />
              <Route path="/products/edge-grips" element={<EdgeGrips />} />
              <Route path="/products/grippers" element={<Grippers />} />
              <Route path="/products/structurelite" element={<StructureLite />} />
              <Route path="/products/hardware-tools" element={<HardwareTools />} />
              <Route path="/product/:handle" element={<ProductDetail />} />
              <Route path="/videos" element={<Videos />} />
              <Route path="/exploded-views" element={<ExplodedViews />} />
              <Route path="/idea-gallery" element={<IdeaGallery />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/mounting-guide" element={<MountingGuide />} />
              <Route path="/industries/museum-display-hardware" element={<MuseumDisplayHardware />} />
              <Route path="/industries/sign-shop-hardware" element={<SignShopHardware />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/checkout-success" element={<CheckoutSuccess />} />
              <Route path="/checkout-canceled" element={<CheckoutCanceled />} />
              <Route path="*" element={<NotFound />} />
            </SentryRoutes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </Sentry.ErrorBoundary>
);

export default App;
